/**
 * AssetForm.jsx  — Edit Asset  (/assets/:id/edit)
 * Full form: Overview · Purchase & Supplier · Warranty & Insurance · Finance
 * All fields pre-populated; image upload; every field the user can edit.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  assetsAPI, mastersAPI, companyAPI, usersAPI, sparesAPI,
} from '../../api';
import toast from 'react-hot-toast';

const IMG_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api','');

/* ─── style helpers ─────────────────────────────────────────── */
const IS = {
  width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
  padding:'8px 11px', fontSize:13, outline:'none',
  boxSizing:'border-box', background:'#fff', color:'#1a1f2e',
};
const LS  = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };
const SHD = {
  fontSize:11, fontWeight:700, color:'rgba(26,31,46,.38)', textTransform:'uppercase',
  letterSpacing:'.07em', marginBottom:8, marginTop:20, paddingBottom:5,
  borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:6,
};
const G2  = { display:'grid', gridTemplateColumns:'1fr 1fr',     gap:'12px 18px' };
const G3  = { display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'12px 18px' };

const PLANT_OPTS  = ['In Service','Under Maintenance','Idle','Scrapped','Transferred','Standby'];
const PAY_OPTS    = ['Fully Paid','Partially Paid','Pending','Credit'];
const STATUS_OPTS = ['operational','maintenance','breakdown','retired'];

const BLANK = () => ({
  name:'', asset_code:'', category_id:'', location_id:'', department:'',
  status:'operational', make:'', model:'', serial_number:'',
  description:'', is_critical:false, assigned_to:'', employee_id:'',
  plant_status:'', finance_status:'Active',
  vendor_id:'', po_number:'',
  our_invoice_no:'', our_invoice_date:'',
  supplier_invoice_no:'', supplier_invoice_date:'',
  purchase_date:'', delivery_date:'', installation_date:'', commission_date:'',
  purchase_cost:'', gst_percent:'', gst_amount:'',
  installation_cost:'', transport_cost:'',
  subsequent_cost_transport:'', subsequent_cost_installation:'',
  payment_terms:'', payment_status:'', capex_code:'', approved_by:'',
  warranty_period:'', warranty_start_date:'', warranty_end_date:'', warranty_expiry:'',
  valid_insurance:'N', valid_insurance_from:'', valid_insurance_to:'',
  valid_amc:'N', valid_amc_from:'', valid_amc_to:'',
  amc_contract:'', amc_start:'', amc_end:'', amc_cost:'',
  dep_type_id:'', depreciation_method:'slm',
  useful_life_years:'10', residual_value:'',
});

/* small field components */
function FInp({ label, col, type='text', value, onChange, placeholder='', mono=false, readOnly=false }) {
  return (
    <div style={{ gridColumn:col }}>
      {label && <label style={LS}>{label}</label>}
      <input type={type} value={value??''} onChange={onChange} readOnly={readOnly}
        placeholder={placeholder}
        style={{ ...IS, fontFamily:mono?"'Courier New',monospace":'inherit',
          background:readOnly?'#f8fafc':'#fff', color:readOnly?'#64748b':'#1a1f2e' }}
        className="af-inp"/>
    </div>
  );
}
function FSel({ label, col, value, onChange, options=[], placeholder='— Select —' }) {
  return (
    <div style={{ gridColumn:col }}>
      {label && <label style={LS}>{label}</label>}
      <select value={value??''} onChange={onChange} className="af-inp" style={{ ...IS }}>
        <option value="">{placeholder}</option>
        {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
      </select>
    </div>
  );
}
function FDate({ label, col, value, onChange }) {
  return (
    <div style={{ gridColumn:col }}>
      {label && <label style={LS}>{label}</label>}
      <input type="date" value={value??''} onChange={onChange} className="af-inp" style={{ ...IS }}/>
    </div>
  );
}
function FNum({ label, col, value, onChange, placeholder='0' }) {
  return (
    <div style={{ gridColumn:col }}>
      {label && <label style={LS}>{label}</label>}
      <input type="number" value={value??''} onChange={onChange} placeholder={placeholder}
        className="af-inp" style={{ ...IS }}/>
    </div>
  );
}
function SH({ children }) {
  return <div style={SHD}>{children}<div style={{ flex:1,height:1,background:'#e2e8f0',marginLeft:4 }}/></div>;
}

const TABS = [
  { id:'overview',  label:'Overview' },
  { id:'purchase',  label:'Purchase & Supplier' },
  { id:'warranty',  label:'Warranty & Insurance' },
  { id:'finance',   label:'Finance' },
];

/* ═══════════════════════════════════════════════════════════════ */
export default function AssetForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form,    setForm]   = useState(BLANK());
  const [tab,     setTab]    = useState('overview');
  const [saving,  setSaving] = useState(false);
  const [loading, setLoading]= useState(isEdit);

  /* masters */
  const [categories, setCategories] = useState([]);
  const [locations,  setLocations]  = useState([]);
  const [departments,setDepartments]= useState([]);
  const [employees,  setEmployees]  = useState([]);
  const [depMethods, setDepMethods] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [warrantyPeriods, setWarrantyPeriods] = useState([]);
  const [depTypes,   setDepTypes]   = useState([]);
  const [vendors,    setVendors]    = useState([]);

  /* image */
  const [subsequentCosts, setSubsequentCosts] = useState([]);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null); // existing server image
  const [localPreview, setLocalPreview] = useState(null); // new file blob
  const fileRef = useRef(null);

  const s = (k,v) => setForm(f=>({...f,[k]:v}));

  /* ── Single effect: masters first, THEN asset (no race) ── */
  useEffect(()=>{
    const sid = (v) => v != null && v !== '' ? String(v) : '';   // number→string for <select>
    const dt  = (v) => v ? String(v).slice(0,10) : '';           // safe date string

    const run = async () => {
      // Step 1 — load ALL masters in parallel
      const [cats, locs, depts, emps, dMethods, dTypes, aClasses, wPeriods] = await Promise.allSettled([
        assetsAPI.categories(),
        mastersAPI.listLocations().catch(() => ({ data: [] })),    // locations table (FK target for assets)
        companyAPI.listDepartments().catch(() => ({ data: [] })),
        mastersAPI.listEmployees().catch(() => ({ data: [] })),
        mastersAPI.listDepMethods().catch(() => ({ data: [] })),
        mastersAPI.listDepTypes().catch(() => ({ data: [] })),
        mastersAPI.listAssetClasses().catch(() => ({ data: [] })),
        mastersAPI.listWarrantyPeriods().catch(() => ({ data: [] })),
      ]);
      if (cats.status    === 'fulfilled') setCategories(cats.value.data || []);
      if (locs.status    === 'fulfilled') setLocations(locs.value.data || []);
      if (depts.status   === 'fulfilled') setDepartments(depts.value.data?.data || depts.value.data || []);
      if (emps.status    === 'fulfilled') setEmployees(emps.value.data || []);
      if (dMethods.status=== 'fulfilled') setDepMethods(dMethods.value.data || []);
      if (dTypes.status    === 'fulfilled') setDepTypes(dTypes.value.data || []);
      if (aClasses.status  === 'fulfilled') setAssetClasses(aClasses.value.data || []);
      if (wPeriods.status  === 'fulfilled') setWarrantyPeriods(wPeriods.value.data || []);

      try {
        const { supplyAPI } = await import('../../api');
        const vr = await supplyAPI?.listVendors?.();
        setVendors(Array.isArray(vr?.data) ? vr.data : (vr?.data?.data || []));
      } catch {}

      // Step 2 — AFTER masters are set, load asset (guarantees options exist when value binds)
      // Load subsequent costs if editing
      if (!isEdit) { setLoading(false); return; }
      try {
        const sc = await assetsAPI.listSubsequentCosts(id);
        setSubsequentCosts(sc.data || []);
      } catch { setSubsequentCosts([]); }

      try {
        const r = await assetsAPI.profile(id);
        const a = r.data;
        if (a.image_url || a.image) {
          const src = a.image_url || (a.image ? `${IMG_BASE}${a.image}` : null);
          if (src) setImagePreview(src);
        }
        setForm({
          name: a.name||'', asset_code: a.asset_code||'',
          // IDs must be strings to match <option value="…"> (HTML is always string)
          category_id:  sid(a.category_id),
          location_id:  sid(a.location_id),
          department:   a.department||'',
          status:       a.status||'operational',
          make: a.make||'', model: a.model||'', serial_number: a.serial_number||'',
          description:  a.description||'', is_critical: !!a.is_critical,
          assigned_to:  sid(a.assigned_to),
          employee_id:  sid(a.employee_id),
          plant_status: a.plant_status||'',
          finance_status: a.finance_status||'Active',
          vendor_id:    sid(a.vendor?.id || a.vendor_id),
          po_number:    a.po_number||'',
          our_invoice_no:         a.our_invoice_no||'',
          our_invoice_date:       dt(a.our_invoice_date),
          supplier_invoice_no:    a.supplier_invoice_no||'',
          supplier_invoice_date:  dt(a.supplier_invoice_date),
          purchase_date:     dt(a.purchase_date),
          delivery_date:     dt(a.delivery_date),
          installation_date: dt(a.installation_date),
          commission_date:   dt(a.commission_date),
          purchase_cost:     a.purchase_cost||'',
          gst_percent:       a.gst_percent||'',
          gst_amount:        a.gst_amount||'',
          installation_cost: a.installation_cost||'',
          transport_cost:    a.transport_cost||'',
          subsequent_cost_transport:    a.subsequent_cost_transport||'',
          subsequent_cost_installation: a.subsequent_cost_installation||'',
          payment_terms:  a.payment_terms||'',
          payment_status: a.payment_status||'',
          capex_code:     a.capex_code||'',
          approved_by:    a.approved_by||'',
          warranty_period:      a.warranty_period||'',
          warranty_start_date:  dt(a.warranty_start_date),
          warranty_end_date:    dt(a.warranty_end_date || a.warranty_expiry),
          warranty_expiry:      dt(a.warranty_expiry),
          valid_insurance:      a.valid_insurance||'N',
          valid_insurance_from: dt(a.valid_insurance_from),
          valid_insurance_to:   dt(a.valid_insurance_to),
          valid_amc:    a.valid_amc||'N',
          valid_amc_from: dt(a.valid_amc_from),
          valid_amc_to:   dt(a.valid_amc_to),
          amc_contract: a.amc_contract||'',
          amc_start:    dt(a.amc_start),
          amc_end:      dt(a.amc_end),
          amc_cost:     a.amc_cost||'',
          dep_type_id:          sid(a.dep_type_id),
          installation_cost_head_yn: a.installation_cost_head_yn||'N',
          transport_cost_head_yn:     a.transport_cost_head_yn||'N',
          asset_class_id:       sid(a.asset_class_id),
          capitalized_date:     dt(a.capitalized_date),
          warranty_period_id:   sid(a.warranty_period_id),
          depreciation_method:  a.depreciation_method||'slm',
          useful_life_years:    String(a.useful_life_years||'10'),
          residual_value:       a.residual_value||'',
        });
      } catch {
        try {
          const r2 = await assetsAPI.get(id);
          const a = r2.data;
          setForm(f => ({
            ...f,
            name: a.name||'', asset_code: a.asset_code||'',
            category_id: sid(a.category_id), location_id: sid(a.location_id),
            department: a.department||'', status: a.status||'operational',
            make: a.make||'', model: a.model||'',
            serial_number: a.serial_number||'',
            description: a.description||'', is_critical: !!a.is_critical,
            assigned_to: sid(a.assigned_to),
            purchase_date: dt(a.purchase_date),
            purchase_cost: a.purchase_cost||'',
            warranty_expiry: dt(a.warranty_expiry),
          }));
        } catch {}
      }
      setLoading(false);
    };

    run();
  }, [id]);   // ← single dep, runs once per asset ID

  /* ── save ───────────────────────────────────────────────── */
  const save = async () => {
    if (!form.name.trim())       { toast.error('Asset Name is required'); return; }
    if (!form.asset_code.trim()) { toast.error('Asset Code is required'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>{
        if (v!=='' && v!==null && v!==undefined) fd.append(k, v);
      });
      if (imageFile) fd.append('image', imageFile);
      // Helper to sync subsequent costs
      const syncSC = async (assetId) => {
        // Always sync so deletions are persisted too
          await assetsAPI.syncSubsequentCosts(assetId, { items: subsequentCosts });
      };
      if (isEdit) {
        await assetsAPI.update(id, fd);
        await syncSC(id);
        toast.success('Asset updated successfully!');
        navigate(`/assets/${id}/profile`);
      } else {
        const r = await assetsAPI.create(fd);
        await syncSC(r.data.id);
        toast.success('Asset created!');
        navigate(`/assets/${r.data.id}/profile`);
      }
    } catch(e) {
      toast.error(e?.response?.data?.detail || e?.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  /* ── finance calc ───────────────────────────────────────── */
  const calcFin = () => {
    const c=parseFloat(form.purchase_cost)||0, i=parseFloat(form.installation_cost)||0;
    const t=parseFloat(form.transport_cost)||0;
    const total=c+i+t, life=parseInt(form.useful_life_years)||10;
    const resid=parseFloat(form.residual_value)||total*.1;
    const annDep=life>0?(total-resid)/life:0;
    const py=form.purchase_date?new Date(form.purchase_date).getFullYear():null;
    const cy=new Date().getFullYear(), yu=py?Math.max(0,cy-py):0;
    const accDep=Math.min(annDep*yu,total-resid), bookVal=Math.max(resid,total-accDep);
    const rate=total>0?((annDep/total)*100).toFixed(1):'0.0';
    const eol=py?new Date(py+life,0,1).toLocaleString('en-IN',{month:'short',year:'numeric'}):'—';
    return { total,annDep,accDep,bookVal,resid,rate,eol };
  };
  const fin = calcFin();
  const fmt = n=>n?`₹${Number(n).toLocaleString('en-IN')}`:'—';
  const selDepType = depTypes.find(dt=>dt.id===parseInt(form.dep_type_id));

  /* ── loading ────────────────────────────────────────────── */
  if (loading) return (
    <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ width:36,height:36,border:'3px solid #e2e8f0',borderTopColor:'#1a56db',
          borderRadius:'50%',animation:'spin .8s linear infinite',margin:'0 auto 12px' }}/>
        <div style={{ fontSize:13,color:'#64748b' }}>Loading asset…</div>
      </div>
    </div>
  );

  /* ══════════════ RENDER ═════════════════════════════════════ */
  return (
    <div style={{ background:'#f8fafc',minHeight:'100vh' }}>
      <style>{`
        .af-inp:focus { border-color:#1a56db !important; box-shadow:0 0 0 3px rgba(26,86,219,.1); outline:none; }
      `}</style>

      {/* HEADER */}
      <div style={{ background:'#fff',borderBottom:'1px solid #e2e8f0',padding:'12px 24px',
        display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:10 }}>
        <div style={{ display:'flex',alignItems:'center',gap:12 }}>
          <button onClick={()=>navigate(isEdit?`/assets/${id}/profile`:'/assets')}
            style={{ background:'none',border:'1px solid #e2e8f0',borderRadius:6,
              padding:'5px 12px',cursor:'pointer',color:'#64748b',fontSize:12 }}>← Back</button>
          <div>
            <div style={{ fontSize:17,fontWeight:700,color:'#1a1f2e' }}>
              {isEdit ? `Edit Asset — ${form.asset_code}` : 'Register New Asset'}
            </div>
            <div style={{ fontSize:12,color:'#94a3b8',marginTop:2 }}>
              {isEdit ? 'Update asset details, costs and configuration' : 'Fill in all required fields and save'}
            </div>
          </div>
        </div>
        <div style={{ display:'flex',gap:8 }}>
          <button onClick={()=>navigate(isEdit?`/assets/${id}/profile`:'/assets')}
            style={{ padding:'7px 16px',border:'1px solid #cbd5e1',borderRadius:7,
              background:'#fff',fontSize:13,cursor:'pointer',color:'#374151' }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ padding:'7px 20px',border:'none',borderRadius:7,
              background:saving?'#93c5fd':'#1a56db',color:'#fff',
              fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer' }}>
            {saving?'Saving…':isEdit?'✓ Update Asset':'✓ Create Asset'}
          </button>
        </div>
      </div>

      {/* TABS */}
      <div style={{ background:'#fff',borderBottom:'1px solid #e2e8f0',
        padding:'0 24px',display:'flex',overflowX:'auto' }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            padding:'10px 16px', fontSize:13,
            fontWeight:tab===t.id?600:400,
            color:tab===t.id?'#1a56db':'#64748b',
            borderBottom:tab===t.id?'2px solid #1a56db':'2px solid transparent',
            background:'none',border:'none',borderRadius:0,cursor:'pointer',
            whiteSpace:'nowrap',marginBottom:-1,
          }}>{t.label}</button>
        ))}
      </div>

      {/* BODY */}
      <div style={{ padding:'20px 24px',maxWidth:1200 }}>

        {/* ── OVERVIEW ──────────────────────────────────────── */}
        {tab==='overview' && (
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr',gap:28 }}>
            <div>
              <SH>Basic Information</SH>
              <div style={G2}>
                <FInp label="Asset Name *" col="1/-1" value={form.name}
                  onChange={e=>s('name',e.target.value)} placeholder="e.g. CNC Machining Centre #1"/>
                <FInp label="Asset Code *" value={form.asset_code}
                  onChange={e=>s('asset_code',e.target.value.toUpperCase())}
                  placeholder="AST-CNC-001" mono readOnly={isEdit}/>
                <FSel label="Status" value={form.status} onChange={e=>s('status',e.target.value)}
                  options={STATUS_OPTS.map(v=>({value:v,label:v.charAt(0).toUpperCase()+v.slice(1)}))}/>
                <FSel label="Category" value={form.category_id} onChange={e=>s('category_id',e.target.value)}
                  options={categories.map(c=>({value:c.id,label:c.name}))} placeholder="— Select Category —"/>
                <FSel label="Location" value={form.location_id} onChange={e=>s('location_id',e.target.value)}
                  options={locations.map(l=>({value:l.id,label:l.location_name||l.name}))} placeholder="— Select Location —"/>
                <FSel label="Department" value={form.department} onChange={e=>s('department',e.target.value)}
                  options={departments.length>0
                    ? departments.map(d=>({value:d.department_name||d.name,label:d.department_name||d.name}))
                    : []} placeholder="— Select / Type Department —"/>
                {departments.length===0 && (
                  <FInp label="Department (type if not in list)" col="1/-1" value={form.department}
                    onChange={e=>s('department',e.target.value)} placeholder="e.g. Production"/>
                )}
                <FSel label="Assigned Employee" col="1/-1" value={form.employee_id}
                  onChange={e=>s('employee_id',e.target.value)}
                  options={employees.map(e=>({value:e.id,label:`${e.name}${e.designation?' — '+e.designation:''}`}))}
                  placeholder="— Select Employee —"/>
                <FInp label="Make / Brand" value={form.make} onChange={e=>s('make',e.target.value)} placeholder="e.g. Makino"/>
                <FInp label="Model" value={form.model} onChange={e=>s('model',e.target.value)}/>
                <FInp label="Serial Number" col="1/-1" value={form.serial_number}
                  onChange={e=>s('serial_number',e.target.value)} mono/>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={LS}>Description</label>
                  <textarea value={form.description} onChange={e=>s('description',e.target.value)}
                    rows={3} placeholder="Additional details…" className="af-inp"
                    style={{ ...IS,resize:'vertical',fontFamily:'inherit' }}/>
                </div>
              </div>

              <SH>Status</SH>
              <div style={G2}>
                <FSel label="Plant Status" value={form.plant_status} onChange={e=>s('plant_status',e.target.value)}
                  options={PLANT_OPTS}/>
                <FSel label="Finance Status" value={form.finance_status} onChange={e=>s('finance_status',e.target.value)}
                  options={[{value:'Active',label:'Active'},{value:'Retired',label:'Retired'}]}/>
              </div>
              <div style={{ display:'flex',alignItems:'center',gap:8,marginTop:12 }}>
                <input type="checkbox" id="iscrit" checked={!!form.is_critical}
                  onChange={e=>s('is_critical',e.target.checked)} style={{ width:15,height:15,cursor:'pointer' }}/>
                <label htmlFor="iscrit" style={{ fontSize:13,color:'#374151',cursor:'pointer' }}>
                  ⚡ Mark as Critical Asset (triggers priority alerts)
                </label>
              </div>
            </div>

            {/* RIGHT: image + quick finance */}
            <div>
              <SH>Asset Image</SH>
              <div style={{ background:'#f8fafc',borderRadius:10,padding:16,border:'1px solid #e2e8f0',marginBottom:20 }}>
                {(localPreview||imagePreview) && (
                  <div style={{ marginBottom:12,position:'relative',display:'inline-block' }}>
                    <img src={localPreview||imagePreview} alt="Asset"
                      style={{ width:'100%',maxHeight:180,objectFit:'cover',borderRadius:8,border:'1px solid #e2e8f0' }}/>
                    {localPreview && (
                      <span style={{ position:'absolute',top:-6,right:-6,background:'#16a34a',color:'#fff',
                        borderRadius:'50%',width:20,height:20,fontSize:11,display:'flex',
                        alignItems:'center',justifyContent:'center',fontWeight:700 }}>✓</span>
                    )}
                  </div>
                )}
                <button onClick={()=>fileRef.current?.click()} style={{ width:'100%',padding:'8px 14px',
                  background:'#fff',border:'1.5px dashed #cbd5e1',borderRadius:7,cursor:'pointer',
                  fontSize:13,color:'#64748b',textAlign:'center' }}>
                  {localPreview||imagePreview ? '🔄 Change Image' : '📷 Upload Asset Image'}
                </button>
                <input ref={fileRef} type="file" accept="image/*" style={{ display:'none' }}
                  onChange={e=>{
                    const f=e.target.files[0];
                    if(f){ setImageFile(f); setLocalPreview(URL.createObjectURL(f)); }
                  }}/>
                {(localPreview||imagePreview) && (
                  <button onClick={()=>{ setImageFile(null); setLocalPreview(null); setImagePreview(null); }}
                    style={{ marginTop:6,width:'100%',padding:'5px',background:'none',
                      border:'1px solid #fee2e2',borderRadius:6,cursor:'pointer',fontSize:11,color:'#dc2626' }}>
                    ✕ Remove Image
                  </button>
                )}
              </div>

              <SH>Finance Preview</SH>
              <div style={{ background:'#f0f6ff',borderRadius:10,padding:'12px 14px',border:'1px solid #dbeafe' }}>
                {[
                  ['Total Cost', fmt(fin.total)],
                  ['Annual Dep.', fmt(fin.annDep)],
                  ['Rate', `${fin.rate}% p.a.`],
                  ['Book Value', fmt(fin.bookVal)],
                  ['End of Life', fin.eol],
                ].map(([l,v])=>(
                  <div key={l} style={{ display:'flex',justifyContent:'space-between',
                    padding:'4px 0',fontSize:12,borderBottom:'1px solid rgba(26,86,219,.08)' }}>
                    <span style={{ color:'#64748b' }}>{l}</span>
                    <span style={{ fontWeight:600,color:'#1a56db' }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── PURCHASE & SUPPLIER ───────────────────────────── */}
        {tab==='purchase' && (
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:28 }}>
            <div>
              <SH>Invoice & Order Details</SH>
              <div style={G2}>
                <FInp label="Purchase Order #" value={form.po_number}
                  onChange={e=>s('po_number',e.target.value)} placeholder="PO-2024-001"/>
                <FDate label="Purchase Date" value={form.purchase_date} onChange={e=>s('purchase_date',e.target.value)}/>
                <FInp label="Our Invoice No" value={form.our_invoice_no}
                  onChange={e=>s('our_invoice_no',e.target.value)} mono/>
                <FDate label="Our Invoice Date" value={form.our_invoice_date} onChange={e=>s('our_invoice_date',e.target.value)}/>
                <FInp label="Supplier Invoice No" value={form.supplier_invoice_no}
                  onChange={e=>s('supplier_invoice_no',e.target.value)} mono/>
                <FDate label="Supplier Invoice Date" value={form.supplier_invoice_date} onChange={e=>s('supplier_invoice_date',e.target.value)}/>
                <FDate label="Delivery Date" value={form.delivery_date} onChange={e=>s('delivery_date',e.target.value)}/>
                <FDate label="Installation Date" value={form.installation_date} onChange={e=>s('installation_date',e.target.value)}/>
                <FDate label="Commissioning Date" col="1/-1" value={form.commission_date} onChange={e=>s('commission_date',e.target.value)}/>
                <FDate label="Capitalised Date" value={form.capitalized_date} onChange={e=>s('capitalized_date',e.target.value)}/>
              
              </div>

              <SH>Cost Details</SH>
              <div style={G2}>
              <FNum label="Acquisition / Purchase Cost (₹)" col="1/-1" value={form.purchase_cost} onChange={e => {const cost = e.target.value; s('purchase_cost', cost); const pct = parseFloat(form.gst_percent);
                  if (pct > 0)
                    s('gst_amount', ((parseFloat(cost)||0) * pct / 100).toFixed(2));
                }}/>
                <FNum label="GST %" placeholder="18"
  value={form.gst_percent}
  onChange={e => {
    const pct = e.target.value;
    s('gst_percent', pct);
    const cost = parseFloat(form.purchase_cost);
    if (cost > 0)
      s('gst_amount', (cost * (parseFloat(pct)||0) / 100).toFixed(2));
  }}/>
                <FNum label="GST Amount (₹)" value={form.gst_amount} onChange={e=>s('gst_amount',e.target.value)}/>
                {/* Installation Cost + YN */}
                <FNum label="Installation Cost (₹)" value={form.installation_cost} onChange={e=>s('installation_cost',e.target.value)}/>
                <div>
                  <label style={LS}>Installation – Cost Head YN</label>
                  <select className="af-inp" style={{...IS}} value={form.installation_cost_head_yn||'N'} onChange={e=>s('installation_cost_head_yn',e.target.value)}>
                    <option value="N">N — Exclude from Cost Head</option>
                    <option value="Y">Y — Include in Cost Head</option>
                  </select>
                </div>
                {/* Transport Cost + YN */}
                <FNum label="Transport Cost (₹)" value={form.transport_cost} onChange={e=>s('transport_cost',e.target.value)}/>
                <div>
                  <label style={LS}>Transport – Cost Head YN</label>
                  <select className="af-inp" style={{...IS}} value={form.transport_cost_head_yn||'N'} onChange={e=>s('transport_cost_head_yn',e.target.value)}>
                    <option value="N">N — Exclude from Cost Head</option>
                    <option value="Y">Y — Include in Cost Head</option>
                  </select>
                </div>
              </div>

              {/* ── SUBSEQUENT COSTS ───────────────────── */}
              <div style={{ marginTop:20 }}>
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8 }}>
                  <span style={{ fontSize:11,fontWeight:700,color:'rgba(26,31,46,.4)',textTransform:'uppercase',letterSpacing:'.07em' }}>Subsequent Costs</span>
                  <button type="button"
                    onClick={()=>setSubsequentCosts(sc=>[...sc,{transport_cost:'',installation_cost:'',description:'',cost_date:'',include_in_cost_head:'N'}])}
                    style={{ padding:'5px 12px',background:'#1a56db',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer' }}>
                    + Add Row
                  </button>
                </div>
                {subsequentCosts.length===0 ? (
                  <div style={{ padding:'12px',color:'#94a3b8',fontSize:13,textAlign:'center',border:'1px dashed #e2e8f0',borderRadius:8 }}>
                    No subsequent costs — click "+ Add Row"
                  </div>
                ) : subsequentCosts.map((sc,i)=>{
                  const upd=(k,v)=>setSubsequentCosts(list=>list.map((x,j)=>j===i?{...x,[k]:v}:x));
                  return (
                    <div key={i} style={{ background:'#f8fafc',borderRadius:10,border:'1px solid #e2e8f0',padding:'12px 14px',marginBottom:8 }}>
                      <div style={{ display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8 }}>
                        <span style={{ fontSize:12,fontWeight:700,color:'#64748b' }}>Row {i+1}</span>
                        <button type="button" onClick={()=>setSubsequentCosts(list=>list.filter((_,j)=>j!==i))}
                          style={{ background:'none',border:'1px solid #fca5a5',borderRadius:5,cursor:'pointer',color:'#dc2626',fontSize:11,padding:'2px 8px' }}>✕ Remove</button>
                      </div>
                      <div style={G2}>
                        <div>
                          <label style={LS}>Transport Cost (₹)</label>
                          <input type="number" className="af-inp" style={{...IS}} value={sc.transport_cost||''} onChange={e=>upd('transport_cost',e.target.value)} placeholder="0"/>
                        </div>
                        <div>
                          <label style={LS}>Installation Cost (₹)</label>
                          <input type="number" className="af-inp" style={{...IS}} value={sc.installation_cost||''} onChange={e=>upd('installation_cost',e.target.value)} placeholder="0"/>
                        </div>
                        <div style={{ gridColumn:'1/-1' }}>
                          <label style={LS}>Description</label>
                          <input className="af-inp" style={{...IS}} value={sc.description||''} onChange={e=>upd('description',e.target.value)} placeholder="e.g. Additional installation at remote site"/>
                        </div>
                        <div>
                          <label style={LS}>Cost Date</label>
                          <input type="date" className="af-inp" style={{...IS}} value={sc.cost_date||''} onChange={e=>upd('cost_date',e.target.value)}/>
                        </div>
                        <div>
                          <label style={LS}>Cost Head YN</label>
                          <select className="af-inp" style={{...IS}} value={sc.include_in_cost_head||'N'} onChange={e=>upd('include_in_cost_head',e.target.value)}>
                            <option value="N">N — Exclude from Cost Head</option>
                            <option value="Y">Y — Include in Cost Head</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Live Cost Head Total Preview */}
              {(()=>{
                const base  = parseFloat(form.purchase_cost)||0;
                const inst  = form.installation_cost_head_yn==='Y'?(parseFloat(form.installation_cost)||0):0;
                const trans = form.transport_cost_head_yn==='Y'?(parseFloat(form.transport_cost)||0):0;
                const subT  = subsequentCosts.filter(sc=>sc.include_in_cost_head==='Y')
                  .reduce((s,sc)=>s+(parseFloat(sc.transport_cost)||0)+(parseFloat(sc.installation_cost)||0),0);
                const total = base+inst+trans+subT;
                return (
                  <div style={{ marginTop:10,background:'#f0f6ff',borderRadius:8,padding:'10px 14px',border:'1px solid #dbeafe' }}>
                    <div style={{ display:'flex',justifyContent:'space-between',fontWeight:800,color:'#1a56db',fontSize:13 }}>
                      <span>Total Capitalised Value (Cost Head)</span>
                      <span style={{ fontFamily:'monospace' }}>₹{total.toLocaleString('en-IN',{minimumFractionDigits:2})}</span>
                    </div>
                    <div style={{ fontSize:11,color:'#64748b',marginTop:3 }}>
                      Base ₹{base.toLocaleString('en-IN')}
                      {inst>0&&<> + Install ₹{inst.toLocaleString('en-IN')}</>}
                      {trans>0&&<> + Transport ₹{trans.toLocaleString('en-IN')}</>}
                      {subT>0&&<> + Subsequent ₹{subT.toLocaleString('en-IN')}</>}
                    </div>
                  </div>
                );
              })()}

              <SH>Payment & Approval</SH>
              <div style={G2}>
                <FInp label="Payment Terms" value={form.payment_terms}
                  onChange={e=>s('payment_terms',e.target.value)} placeholder="Net 30"/>
                <FSel label="Payment Status" value={form.payment_status}
                  onChange={e=>s('payment_status',e.target.value)} options={PAY_OPTS}/>
                <FInp label="CapEx Code" value={form.capex_code}
                  onChange={e=>s('capex_code',e.target.value)} mono/>
                <FInp label="Approved By" value={form.approved_by}
                  onChange={e=>s('approved_by',e.target.value)}/>
              </div>
            </div>

            <div>
              <SH>Supplier / Vendor</SH>
              <FSel label="Select Vendor" value={form.vendor_id}
                onChange={e=>s('vendor_id',e.target.value)}
                options={vendors.map(v=>({value:v.id,label:`${v.name} (${v.vendor_code})`}))}
                placeholder="— Select Vendor —"/>
              {form.vendor_id && (() => {
                const v=vendors.find(vv=>vv.id===parseInt(form.vendor_id));
                return v ? (
                  <div style={{ marginTop:12,background:'#f0f6ff',borderRadius:10,padding:'12px 14px',border:'1px solid #dbeafe' }}>
                    <div style={{ fontWeight:700,color:'#1a56db',fontSize:14,marginBottom:4 }}>🏭 {v.name}</div>
                    <div style={{ fontSize:12,color:'#64748b' }}>{v.vendor_code}{v.email&&` · ${v.email}`}</div>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {/* ── WARRANTY & INSURANCE ──────────────────────────── */}
        {tab==='warranty' && (
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:28 }}>
            <div>
              <SH>Warranty</SH>
              <div style={G2}>
              <FSel  label="Warranty Period" value={form.warranty_period_id}
                  onChange={e=>s('warranty_period_id',e.target.value)}
                  options={warrantyPeriods.map(wp=>({value:wp.id,label:`${wp.code} — ${wp.warranty_name}`}))}
                  placeholder="— Select Warranty Period —"/>
                {/* <FInp label="Warranty Period" col="1/-1" value={form.warranty_period}
                  onChange={e=>s('warranty_period',e.target.value)} placeholder="e.g. 12 Months"/> */}
                  </div> <div style={G2}>
                <FDate label="Warranty Start Date" value={form.warranty_start_date} onChange={e=>s('warranty_start_date',e.target.value)}/>
                <FDate label="Warranty End Date"   value={form.warranty_end_date}   onChange={e=>s('warranty_end_date',e.target.value)}/>
              </div>

              <SH>Insurance</SH>
              <FSel label="Valid Insurance" value={form.valid_insurance}
                onChange={e=>s('valid_insurance',e.target.value)}
                options={[{value:'N',label:'N — Not Available'},{value:'Y',label:'Y — Valid Insurance Exists'}]}/>
              {form.valid_insurance==='Y' && (
                <div style={{ ...G2,marginTop:12 }}>
                  <FDate label="Insurance From Date" value={form.valid_insurance_from} onChange={e=>s('valid_insurance_from',e.target.value)}/>
                  <FDate label="Insurance To Date"   value={form.valid_insurance_to}   onChange={e=>s('valid_insurance_to',e.target.value)}/>
                </div>
              )}
            </div>
            <div>
              <SH>AMC / Service Contract</SH>
              <FSel label="Valid AMC" value={form.valid_amc}
                onChange={e=>s('valid_amc',e.target.value)}
                options={[{value:'N',label:'N — Not Available'},{value:'Y',label:'Y — Valid AMC Exists'}]}/>
              {form.valid_amc==='Y' && (
                <div style={{ ...G2,marginTop:12 }}>
                  <FDate label="AMC From Date" value={form.valid_amc_from} onChange={e=>s('valid_amc_from',e.target.value)}/>
                  <FDate label="AMC To Date"   value={form.valid_amc_to}   onChange={e=>s('valid_amc_to',e.target.value)}/>
                  <FInp  label="AMC Contract #"  value={form.amc_contract}  onChange={e=>s('amc_contract',e.target.value)}/>
                  <FNum  label="AMC Annual Cost (₹)" value={form.amc_cost}   onChange={e=>s('amc_cost',e.target.value)}/>
                  <FDate label="AMC Start Date" value={form.amc_start} onChange={e=>s('amc_start',e.target.value)}/>
                  <FDate label="AMC End Date"   value={form.amc_end}   onChange={e=>s('amc_end',e.target.value)}/>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── FINANCE ───────────────────────────────────────── */}
        {tab==='finance' && (() => {
          const selAssetClass = assetClasses.find(ac => ac.id === parseInt(form.asset_class_id));
          const handleAssetClassChange = (val) => {
            s('asset_class_id', val);
            const ac = assetClasses.find(ac => ac.id === parseInt(val));
            if (ac) {
              if (ac.dep_type_id) s('dep_type_id', String(ac.dep_type_id));
              if (ac.useful_life_months) s('useful_life_years', String(Math.round(ac.useful_life_months/12)));
            }
          };
          return (
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:28 }}>
            <div>
              <SH>Asset Class</SH>
              <div style={G2}>
                <FSel label="Asset Class" col="1/-1" value={form.asset_class_id}
                  onChange={e=>handleAssetClassChange(e.target.value)}
                  options={assetClasses.map(ac=>({value:ac.id,label:`${ac.code} — ${ac.description} (${ac.dep_area})`}))}
                  placeholder="— Select Asset Class —"/>
                {selAssetClass && (
                  <div style={{ gridColumn:'1/-1',background:'#f0f6ff',borderRadius:8,
                    padding:'10px 14px',fontSize:12,color:'#1a56db' }}>
                    <strong>Depreciation Area:</strong> {selAssetClass.dep_area}&nbsp;·&nbsp;
                    <strong>Useful Life:</strong> {selAssetClass.useful_life_months} months
                    {selAssetClass.dep_type_desc&&<>&nbsp;·&nbsp;<strong>Dep. Type:</strong> {selAssetClass.dep_type_desc}</>}
                  </div>
                )}
              </div>

              <SH>Depreciation Settings</SH>
              <div style={G2}>
                <FSel label="Depreciation Type" col="1/-1" value={form.dep_type_id}
                  onChange={e=>s('dep_type_id',e.target.value)}
                  options={depTypes.map(dt=>({value:dt.id,label:`${dt.code} — ${dt.description} (${dt.method_code||''})`}))}
                  placeholder="— Select Depreciation Type —"/>
                {selDepType && (
                  <div style={{ gridColumn:'1/-1',background:'#f0f6ff',borderRadius:8,
                    padding:'10px 14px',fontSize:12,color:'#1a56db' }}>
                    <strong>Method:</strong> {selDepType.method_code} &nbsp;·&nbsp;
                    <strong>Calc:</strong> {selDepType.calculation_method||'—'} &nbsp;·&nbsp;
                    <strong>Base:</strong> {selDepType.calculation_base==='M'?'Monthly':'Yearly'}
                    {selDepType.salvage_value_pct!=null&&<>&nbsp;·&nbsp;<strong>Salvage:</strong> {selDepType.salvage_value_pct}%</>}
                  </div>
                )}
                <FNum label="Useful Life (Years)" value={form.useful_life_years}
                  onChange={e=>s('useful_life_years',e.target.value)} placeholder="10"/>
                <FNum label="Residual / Salvage Value (₹)" value={form.residual_value}
                  onChange={e=>s('residual_value',e.target.value)} placeholder="0"/>
              </div>

              <SH>Cost Summary (Calculated)</SH>
              <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <thead><tr style={{ background:'#f8fafc' }}>
                    <th style={{ padding:'9px 14px',textAlign:'left',fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',borderBottom:'1px solid #e2e8f0' }}>Cost Head</th>
                    <th style={{ padding:'9px 14px',textAlign:'right',fontSize:11,fontWeight:700,color:'#64748b',textTransform:'uppercase',borderBottom:'1px solid #e2e8f0' }}>Amount</th>
                  </tr></thead>
                  <tbody>
                    {[
                      ['Acquisition & Production Costs', parseFloat(form.purchase_cost)||0],
                      ['Subsequent Cost – Transport',    parseFloat(form.transport_cost)||0],
                      ['Subsequent Cost – Installation', parseFloat(form.installation_cost)||0],
                    ].map(([l,v])=>(
                      <tr key={l} style={{ borderBottom:'1px solid #f1f5f9' }}>
                        <td style={{ padding:'8px 14px',fontSize:13,color:'#374151' }}>{l}</td>
                        <td style={{ padding:'8px 14px',fontSize:13,textAlign:'right',fontFamily:'monospace' }}>{v?`₹${v.toLocaleString('en-IN')}`:'—'}</td>
                      </tr>
                    ))}
                    <tr style={{ borderTop:'2px solid #1a56db',background:'#f0f6ff' }}>
                      <td style={{ padding:'9px 14px',fontSize:13,fontWeight:800,color:'#1a56db' }}>Total Capitalised Value</td>
                      <td style={{ padding:'9px 14px',fontSize:14,fontWeight:800,color:'#1a56db',textAlign:'right',fontFamily:'monospace' }}>{fmt(fin.total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <SH>Depreciation & Book Value (Preview)</SH>
              <div style={{ border:'1px solid #e2e8f0',borderRadius:10,overflow:'hidden' }}>
                <table style={{ width:'100%',borderCollapse:'collapse' }}>
                  <tbody>
                    {[
                      ['Depreciation Method', (form.depreciation_method||'SLM').toUpperCase()],
                      ['Useful Life', `${form.useful_life_years||10} Years`],
                      ['CFY Annual Depreciation', fmt(fin.annDep)],
                      ['Depreciation Rate', `${fin.rate}% p.a.`],
                      ['Accumulated Depreciation', fmt(fin.accDep)],
                    ].map(([l,v])=>(
                      <tr key={l} style={{ borderBottom:'1px solid #f1f5f9' }}>
                        <td style={{ padding:'8px 14px',fontSize:13,color:'#64748b' }}>{l}</td>
                        <td style={{ padding:'8px 14px',fontSize:13,fontWeight:600,textAlign:'right' }}>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding:'10px 14px',background:'#f0f6ff',borderTop:'2px solid #1a56db',
                  display:'flex',justifyContent:'space-between',alignItems:'center' }}>
                  <span style={{ fontSize:13,fontWeight:700 }}>
                    Current Book Value ({new Date().toLocaleString('en-IN',{month:'short',year:'numeric'})})
                  </span>
                  <span style={{ fontSize:15,fontWeight:800,color:'#1a56db',fontFamily:'monospace' }}>{fmt(fin.bookVal)}</span>
                </div>
                <div style={{ padding:'8px 14px',borderTop:'1px solid #e2e8f0',
                  display:'flex',justifyContent:'space-between',fontSize:12,color:'#64748b' }}>
                  <span>End of Useful Life</span><span>{fin.eol}</span>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Sticky bottom save bar */}
        <div style={{ marginTop:32,padding:'14px 0',borderTop:'1px solid #e2e8f0',
          display:'flex',gap:10,justifyContent:'flex-end' }}>
          <button onClick={()=>navigate(isEdit?`/assets/${id}/profile`:'/assets')}
            style={{ padding:'8px 18px',border:'1px solid #cbd5e1',borderRadius:7,
              background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ padding:'8px 22px',border:'none',borderRadius:7,
              background:saving?'#93c5fd':'#1a56db',color:'#fff',
              fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer' }}>
            {saving?'Saving…':isEdit?'✓ Update Asset':'✓ Create Asset'}
          </button>
        </div>

      </div>{/* end body */}
    </div>
  );
}
